<template>
  <div class="project-detail container py-5">
    <div v-if="loading" class="text-center py-5 small text-muted">Loading…</div>
    <div v-else-if="error" class="text-center py-5 text-danger">Failed to load project.</div>

    <div v-else-if="project">
      <!-- header: catalogName — title on one line (smaller) -->
      <header class="text-center mb-4">
        <div class="project-topline">
          <span class="catalog-name">{{ project.catalogName }}</span>
          <span class="sep"> — </span>
          <span class="project-title-inline">{{ project.title }}</span>
        </div>
      </header>

      <!-- media area: video OR image -->
      <div class="media-area text-center mb-4">
        <video
          v-if="project && project.isVideo"
          ref="caseVideoRef"
          :src="`/projects/${project.projectId}.mp4`"
          :poster="`/projects/thumbs/${project.projectId}.png`"
          muted
          controls
          playsinline
          class="project-video"
        />
        <img
          v-else
          :src="imageSrc"
          :alt="project.title"
          class="project-image"
          @error="onImageError"
        />
      </div>

      <!-- description and details -->
      <section class="project-body">
        <article class="project-desc" v-html="project.description"></article>
      </section>

      <!-- bottom navigation: Previous / Next -->
      <nav class="project-nav d-flex justify-content-between align-items-center mt-5">
        <button
          class="nav-link prev"
          :disabled="!prevId"
          @click="navigateTo(prevId)"
          v-if="prevId"
          title="Previous"
        >
          Previous
        </button>
        <div v-else class="nav-filler"></div>

        <button
          class="nav-link next"
          :disabled="!nextId"
          @click="navigateTo(nextId)"
          v-if="nextId"
          title="Next"
        >
        Next
        </button>
      </nav>
    </div>

    <div v-else class="text-center py-5 small text-muted">Project not found.</div>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import apiClient from '@/apiClient';
import defaultImg from '@/assets/defaultError.png';
import { TIPSTAG_ERROR } from '@/constants';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();

// state
const project = ref(history.state?.preloadedProject || null);
const loading = ref(!project.value);
const error = ref(false);
const imageSrc = ref('');
// navigation
const prevId = ref(null);
const nextId = ref(null);

// video element ref
const caseVideoRef = ref(null);

// keep imageSrc in sync with project data
watch(project, (p) => {
  if (!p) {
    imageSrc.value = defaultImg;
    return;
  }
  imageSrc.value = `/projects/${p.projectId}.png` || defaultImg;
});

// auto-play muted when a video project is loaded; pause/reset when not
watch(project, async (p) => {
  // wait DOM update so video element is mounted/updated
  await nextTick();

  const v = caseVideoRef.value;
  if (p && p.isVideo && v) {
    try {
      v.muted = true;            // start muted, the user can unmute with the player controls
      v.currentTime = 0;
      // attempt to play; ignore play promise rejection
      await v.play().catch(() => {});
    } catch (e) {
      // ignore playback errors
    }
  } else if (v) {
    try {
      v.pause();
      v.currentTime = 0;
    } catch (e) { /* ignore */ }
  }
});

// handle image load errors by replacing with default image
function onImageError(e) {
  e.target.onerror = null;
  e.target.src = defaultImg;
}

// load single project detail and try to determine prev/next
async function loadProject(id, isLogin = false) {
  loading.value = true;
  error.value = false;
  prevId.value = nextId.value = null;
  try {
    const response = await apiClient.post('/Main/getExpProjectDetail', {
       projectId: id,
       isLogin: !!isLogin
    });
    if (response && response.data) {
      project.value = response.data.projectDetail || response.data;
      // try reading prev/next provided by backend
      prevId.value = response.data.previousProjectId || project.value.previousProjectId || null;
      nextId.value = response.data.nextProjectId || project.value.nextProjectId || null;      
    } else {
      project.value = null;
    }
  } catch (e) {
    if (e.response && e.response.data && e.response.status === 400) {
      proxy.$handleTips(e.response.data.tag, e.response.data.msg, e);
    } else {
      proxy.$handleTips(TIPSTAG_ERROR, 'Network error occurred. Please try again later.', e);
    }
    error.value = true;
  } finally {
    loading.value = false;
  }
}

// navigate to another projectId in-place (update route so URL changes)
function navigateTo(id) {
  if (!id) return;
  // push new route param; component watches route param and loads project
  router.push({ name: route.name || 'ExperienceDetail', params: { projectId: id, isLogin: false } }).catch(() => {});
}

// watch route param changes and load
watch(
  () => [route.params.projectId, route.params.isLogin, route.query.isLogin],
  (vals) => {
    const [id, paramIsLogin, queryIsLogin] = vals;
    if (id) {
      // 先从 params 中取 isLogin（优先），若没有则看 query
      const isLoginRaw = paramIsLogin !== undefined ? paramIsLogin : queryIsLogin;
      const isLogin = parseIsLoginParam(isLoginRaw);
      project.value = null;
      loadProject(id, isLogin);
    }
  },
  { immediate: true }
);


function pauseAllCaseVideos() {
  try {
    const vids = document.querySelectorAll('.case-media video');
    vids.forEach(v => {
      try {
        v.pause();
        v.currentTime = 0;
      } catch (e) { /* ignore */ }
    });
  } catch (e) { /* ignore */ }
}

onMounted(async () => {
  // initial load handled by watch(immediate)
});

onBeforeUnmount(() => {
  pauseAllCaseVideos();
});

// helper: 将 route param 转为布尔（"true" / "1" / true -> true）
function parseIsLoginParam(val) {
  if (val === undefined || val === null) return false;
  if (typeof val === 'boolean') return val;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

</script>

<style scoped>
.project-detail { color: rgba(255,255,255,0.95); max-width: 1100px; margin: 0 auto; }

/* new/topline styles: catalogName and title on one line */
.project-topline {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 1.3rem;               /* slightly smaller than h1 */
  color: rgba(255,255,255,0.94);
  max-width: 100%;
  text-align: center;  
}

.catalog-name {
  color: rgba(255,255,255,0.75);    /* muted relative to title */
  font-weight: 500;
}

.sep {
  color: rgba(255,255,255,0.48);
  margin: 0 6px;
}

.project-title-inline {
  color: #ffffff;                   /* bright white for the title */
  font-weight: 500;
  line-height: 1.1;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* keep existing responsive behaviour but allow wrapping on small screens */
@media (max-width: 767.98px) {
  .project-topline { font-size: 0.98rem; }
  .project-title-inline { white-space: normal; max-width: 100%; }
}

/* media */
.media-area { display: flex; align-items: center; justify-content: center; }
.project-image, .project-video {
  max-width: 100%;
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 12px 36px rgba(2,6,23,0.6);
  background: rgba(6,18,28,0.6);
}

/* gallery */
.gallery-grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}
.gallery-thumb {
  width: 140px;
  height: 90px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid rgba(255,255,255,0.04);
  box-shadow: 0 6px 18px rgba(2,6,23,0.45);
}

/* text content */
.project-subtitle {
  color: rgba(255,255,255,0.76);
  font-style: italic;
  margin-bottom: 12px;
}
.project-desc {
  color: rgba(255,255,255,0.92);
  line-height: 1.8;
  font-size: 1rem;
  font-weight: 300;
  padding-left: 10px;
}

/* lightbox */
.lightbox-backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.7);
  z-index: 2500;
}
.lightbox {
  position: relative;
  max-width: 92%;
  max-height: 92%;
}
.lightbox img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
}
.lightbox .close {
  position: absolute;
  top: 8px;
  right: 8px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
}

.project-nav {
  border-top: 1px solid rgba(255,255,255,0.04);
  padding-top: 18px;
  gap: 12px;
}
.nav-link {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.92);
  font-weight: 400;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.nav-link:disabled {
  opacity: 0.45;
  cursor: default;
}
.nav-title {
  color: rgba(255,255,255,0.72);
  font-weight: 400;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nav-filler { flex: 1; }
.project-nav .prev { justify-content: flex-start; }
.project-nav .next { justify-content: flex-end; }

.nav-link.prev::before {
  content: '<<';               /* Left arrow */
  margin-right: 0px;
  color: rgba(255,255,255,0.92);
  font-weight: 400;
  font-size: 1.05em;
  line-height: 1;
}
.nav-link.next::after {
  content: '>>';               /* Right arrow */
  margin-left: 0px;
  color: rgba(255,255,255,0.92);
  font-weight: 400;
  font-size: 1.05em;
  line-height: 1;
}

/* Keep button text aligned with arrows */
.nav-link { display: inline-flex; align-items: center; }
</style>